import { useCallback, useMemo, useState } from 'react';
import { apiErrorText, mailApi } from '@/shared/api';
import { EmailIcon } from '@/shared/config';
import { confirmDialog, toast } from '@/shared/ui';
import type { ChatMailBridge } from '../TicketChat';
import type { MailAction } from '../chat/ChatMessage';
import { useTicketMail } from './useTicketMail';
import { OriginalEmailDialog } from './OriginalEmailDialog';
import { MoveMessageDialog } from './MoveMessageDialog';

interface Options {
  ticketId: string | null | undefined;
  enabled: boolean;
  canMove: boolean;
}

export function useTicketMailBridge({ ticketId, enabled, canMove }: Options) {
  const { info, refreshSoon } = useTicketMail(ticketId, enabled);
  const [originalId, setOriginalId] = useState<string | null>(null);
  const [moveId, setMoveId] = useState<string | null>(null);

  const confirmEmail = useCallback(async () => {
    if (!info?.requester?.address) return false;
    return confirmDialog({
      title: 'Отправить сообщение письмом?',
      description: info.requester.isContact
        ? 'Заявитель в портал не входит и увидит ответ только в почте.'
        : 'Сообщение останется в чате и уйдёт заявителю письмом.',
      items: [`Кому: ${info.requester.address}`, ...info.cc.map((c) => `Копия: ${c.address}`)],
      Icon: EmailIcon,
      tone: 'info',
      confirmLabel: 'Отправить',
    });
  }, [info]);

  const sendAsEmail = useCallback(
    async (messageId: string, resend = false) => {
      if (!ticketId) return;
      try {
        const res = await mailApi.sendMessageAsEmail(ticketId, messageId, resend);
        toast.success(`Письмо поставлено в отправку: ${res.data.to.join(', ')}`);
      } catch (err) {
        toast.error(apiErrorText(err, 'Не удалось отправить письмом'));
      } finally {
        refreshSoon();
      }
    },
    [ticketId, refreshSoon],
  );

  const onMailAction = useCallback(
    (action: MailAction, messageId: string) => {
      if (action === 'original') setOriginalId(messageId);
      else if (action === 'move') setMoveId(messageId);
      else {
        void (async () => {
          if (!(await confirmEmail())) return;
          const already = (info?.messages[messageId]?.outbound.length ?? 0) > 0;
          await sendAsEmail(messageId, already);
        })();
      }
    },
    [confirmEmail, sendAsEmail, info],
  );

  const bridge = useMemo<ChatMailBridge | undefined>(
    () =>
      enabled
        ? {
            info,
            emailByDefault: info?.channel === 'email',
            canMove,
            confirmEmail,
            sendAsEmail: (id: string) => sendAsEmail(id),
            onMailAction,
          }
        : undefined,
    [enabled, info, canMove, confirmEmail, sendAsEmail, onMailAction],
  );

  const dialogs = ticketId ? (
    <>
      <OriginalEmailDialog ticketId={ticketId} messageId={originalId} onClose={() => setOriginalId(null)} />
      <MoveMessageDialog
        ticketId={ticketId}
        messageId={moveId}
        onClose={() => setMoveId(null)}
        onMoved={() => refreshSoon()}
      />
    </>
  ) : null;

  return { info, bridge, dialogs };
}
